// A3 — «кто пишет прямо сейчас»: live dashboard on top of the pool table.
// Admin + manager only (same bar as PaceCard / GET /live-now).
//
// Polls every 20s, but ONLY while the tab is visible — a background tab must
// not keep hammering the worker (Тим, 12.08). The elapsed clock next to each
// active recording ticks every second locally, off the last poll's
// `startedAt`, so it doesn't need a network round trip to move.
import { useEffect, useState } from 'preact/hooks';
import { api, type VistaLiveNowRes, type VistaStaffMe as Me } from '../api';
import type { T } from '../i18n';

const POLL_MS = 20_000;

const fmtH = (h: number): string => h.toFixed(h >= 100 ? 0 : 1);

const fmtElapsed = (startedAt: number): string => {
  const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

const fmtMb = (t: T, bytes: number): string => t('live.mb', { n: Math.round(bytes / (1024 * 1024)) });

/** «1 пишет» / «2 пишут» / «5 пишут» — в русском счётное слово меняется, в
 *  английском нет: словарь отдаёт обе формы, правило выбора живёт здесь. */
const writersWord = (t: T, n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return t('live.recording.one');
  return t('live.recording.many');
};

export function LiveNow({ me, t }: { me: Me; t: T }) {
  const canSee = me.role === 'manager' || me.role === 'admin';
  const [data, setData] = useState<VistaLiveNowRes | null>(null);
  const [, retick] = useState(0);

  // Poll while the tab is visible; stop the timer entirely when it isn't —
  // a paused interval, not just a skipped fetch, so a laptop with ten stale
  // tabs open doesn't keep twenty timers alive in the background.
  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    let timer: ReturnType<typeof setInterval> | null = null;

    const load = () => {
      api
        .liveNow()
        .then((r) => {
          if (alive) setData(r);
        })
        .catch(() => {});
    };

    const start = () => {
      if (timer != null) return;
      load();
      timer = setInterval(load, POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      alive = false;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [canSee]);

  // Local per-second re-render for the "сколько уже пишет" clocks — purely
  // cosmetic, no network, and it stops on its own once nobody is recording.
  const recordingCount = data?.recording.length ?? 0;
  useEffect(() => {
    if (!canSee || recordingCount === 0) return;
    const id = setInterval(() => retick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [canSee, recordingCount]);

  if (!canSee) return null;

  const recording = data?.recording ?? [];
  const live = recording.length > 0;

  return (
    <div class="panel live-now">
      <div class="panel-head">
        <h3 class="live-now-head">
          {live ? (
            <>
              <span class="live-dot" aria-hidden="true" />
              {t('live.now', { n: recording.length, word: writersWord(t, recording.length) })}
            </>
          ) : (
            <span class="muted">{t('live.nobody')}</span>
          )}
        </h3>
      </div>

      {live && (
        <ul class="live-now-list">
          {recording.map((r) => (
            <li key={r.contributorId} class="live-now-row">
              <span class="live-now-name">{r.name ?? r.email}</span>
              <span class="live-now-sep">·</span>
              <span class="live-now-task">{r.taskSlug ?? '—'}</span>
              <span class="live-now-sep">·</span>
              <span class="live-now-elapsed">{fmtElapsed(r.startedAt)}</span>
              <span class="live-now-sep">·</span>
              <span class="live-now-bytes">{fmtMb(t, r.bytesReceived)}</span>
            </li>
          ))}
        </ul>
      )}

      {data && (
        <div class="tiles live-now-tiles">
          <div class="tile">
            <div class="n">{data.today.clips}</div>
            <div class="t">{t('live.clipsToday')}</div>
          </div>
          <div class="tile">
            <div class="n">
              {fmtH(data.today.hours)} {t('pool.hours')}
            </div>
            <div class="t">{t('live.filmedToday')}</div>
          </div>
          <div class="tile">
            <div class="n">{data.activeToday}</div>
            <div class="t">{t('live.peopleFilmed')}</div>
          </div>
        </div>
      )}
    </div>
  );
}
